// Minimal binary glTF (GLB) read / edit / write, VRM-aware.
//
// The avatar builder edits a VRM as glTF JSON + one binary buffer instead of
// round-tripping it through three.js: three has no VRM exporter, and editing
// in place keeps every VRM extension (humanoid, blend shapes, spring bones,
// MToon material properties, license meta) exactly as the base authored it.
//
// Works in the browser and in Node (the base-prep script uses it too): no DOM,
// no three.js.

export interface Glb {
  json: any;
  /** The BIN chunk (buffer 0). */
  bin: Uint8Array;
}

const MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;

export function readGlb(input: ArrayBuffer | Uint8Array): Glb {
  const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (dv.getUint32(0, true) !== MAGIC) throw new Error('Not a binary glTF / VRM file');
  let off = 12;
  let json: any = null;
  let bin: Uint8Array = new Uint8Array(0);
  while (off + 8 <= u8.byteLength) {
    const len = dv.getUint32(off, true);
    const type = dv.getUint32(off + 4, true);
    const body = u8.subarray(off + 8, off + 8 + len);
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(body));
    else if (type === CHUNK_BIN) bin = body;
    off += 8 + len;
  }
  if (!json) throw new Error('GLB has no JSON chunk');
  return { json, bin };
}

export function writeGlb(g: Glb): Uint8Array {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(g.json));
  const jsonPad = (4 - (jsonBytes.length % 4)) % 4;
  const binPad = (4 - (g.bin.length % 4)) % 4;
  const hasBin = g.bin.length > 0;
  const total = 12 + 8 + jsonBytes.length + jsonPad + (hasBin ? 8 + g.bin.length + binPad : 0);
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, jsonBytes.length + jsonPad, true);
  dv.setUint32(16, CHUNK_JSON, true);
  out.set(jsonBytes, 20);
  out.fill(0x20, 20 + jsonBytes.length, 20 + jsonBytes.length + jsonPad);
  if (hasBin) {
    const o = 20 + jsonBytes.length + jsonPad;
    dv.setUint32(o, g.bin.length + binPad, true);
    dv.setUint32(o + 4, CHUNK_BIN, true);
    out.set(g.bin, o + 8);
  }
  return out;
}

/** Bytes of one buffer view. */
export function viewBytes(g: Glb, bufferView: number): Uint8Array {
  const bv = g.json.bufferViews[bufferView];
  return g.bin.subarray(bv.byteOffset || 0, (bv.byteOffset || 0) + bv.byteLength);
}

export function imageBytes(g: Glb, image: number): Uint8Array {
  return viewBytes(g, g.json.images[image].bufferView);
}

const COMPONENTS: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT4: 16 };

/** Read an accessor as a flat float array (non-sparse, non-normalized ints are returned as-is). */
export function readAccessor(g: Glb, index: number): Float32Array | Uint32Array | Uint16Array | Uint8Array {
  const a = g.json.accessors[index];
  const n = COMPONENTS[a.type] * a.count;
  const bv = g.json.bufferViews[a.bufferView];
  const start = (bv.byteOffset || 0) + (a.byteOffset || 0);
  const ctor = a.componentType === 5126 ? Float32Array : a.componentType === 5125 ? Uint32Array : a.componentType === 5123 ? Uint16Array : Uint8Array;
  const size = ctor.BYTES_PER_ELEMENT;
  const stride = bv.byteStride || COMPONENTS[a.type] * size;
  const out = new ctor(n);
  const dv = new DataView(g.bin.buffer, g.bin.byteOffset, g.bin.byteLength);
  const comps = COMPONENTS[a.type];
  for (let i = 0; i < a.count; i++) {
    for (let c = 0; c < comps; c++) {
      const p = start + i * stride + c * size;
      out[i * comps + c] = size === 4 ? (a.componentType === 5126 ? dv.getFloat32(p, true) : dv.getUint32(p, true)) : size === 2 ? dv.getUint16(p, true) : dv.getUint8(p);
    }
  }
  return out;
}

/**
 * Accumulates edits, then `finish()` re-packs the binary buffer with only the
 * views still referenced (so replaced or dropped images free their bytes).
 */
export class GlbEditor {
  json: any;
  private src: Uint8Array;
  /** bufferView index → replacement bytes (for views that existed). */
  private replaced = new Map<number, Uint8Array>();
  /** bufferView index → bytes (for views added by the editor). */
  private added = new Map<number, Uint8Array>();

  constructor(g: Glb) {
    this.json = JSON.parse(JSON.stringify(g.json));
    this.src = g.bin;
    const j = this.json;
    for (const k of ['nodes', 'meshes', 'materials', 'textures', 'images', 'samplers', 'accessors', 'bufferViews', 'skins']) j[k] ||= [];
  }

  addView(bytes: Uint8Array, target?: number): number {
    const i = this.json.bufferViews.length;
    this.json.bufferViews.push({ buffer: 0, byteOffset: 0, byteLength: bytes.byteLength, ...(target ? { target } : {}) });
    this.added.set(i, bytes);
    return i;
  }

  /** Add a tightly packed accessor. `min/max` are computed for VEC3 positions when asked. */
  addAccessor(data: Float32Array | Uint32Array | Uint16Array | Uint8Array, type: keyof typeof COMPONENTS, opts: { target?: number; minmax?: boolean; normalized?: boolean } = {}): number {
    const componentType = data instanceof Float32Array ? 5126 : data instanceof Uint32Array ? 5125 : data instanceof Uint16Array ? 5123 : 5121;
    const view = this.addView(new Uint8Array(data.buffer, data.byteOffset, data.byteLength), opts.target);
    const comps = COMPONENTS[type];
    const acc: any = { bufferView: view, componentType, count: data.length / comps, type };
    if (opts.normalized) acc.normalized = true;
    if (opts.minmax) {
      const min = new Array(comps).fill(Infinity), max = new Array(comps).fill(-Infinity);
      for (let i = 0; i < data.length; i++) { const c = i % comps; if (data[i] < min[c]) min[c] = data[i]; if (data[i] > max[c]) max[c] = data[i]; }
      acc.min = min; acc.max = max;
    }
    this.json.accessors.push(acc);
    return this.json.accessors.length - 1;
  }

  addImage(bytes: Uint8Array, mimeType: string, name: string): number {
    const view = this.addView(bytes);
    this.json.images.push({ name, bufferView: view, mimeType });
    return this.json.images.length - 1;
  }

  /** New texture (sampler 0 = linear/repeat, which every VRoid export has). */
  addTexture(image: number, sampler = 0): number {
    if (!this.json.samplers.length) this.json.samplers.push({ magFilter: 9729, minFilter: 9729, wrapS: 10497, wrapT: 10497 });
    this.json.textures.push({ sampler, source: image });
    return this.json.textures.length - 1;
  }

  replaceImage(image: number, bytes: Uint8Array, mimeType: string) {
    const im = this.json.images[image];
    const bv = im.bufferView;
    if (this.added.has(bv)) this.added.set(bv, bytes); else this.replaced.set(bv, bytes);
    this.json.bufferViews[bv].byteLength = bytes.byteLength;
    im.mimeType = mimeType;
  }

  bytesOfView(bv: number): Uint8Array {
    const a = this.added.get(bv) ?? this.replaced.get(bv);
    if (a) return a;
    const v = this.json.bufferViews[bv];
    return this.src.subarray(v.byteOffset || 0, (v.byteOffset || 0) + v.byteLength);
  }

  imageBytes(image: number): Uint8Array {
    return this.bytesOfView(this.json.images[image].bufferView);
  }

  /**
   * Drop unreferenced accessors, textures, images and buffer views (after a
   * mesh/material was removed), then pack one buffer.
   */
  finish(): Glb {
    const j = this.json;
    // --- accessors in use ---
    const accUsed = new Set<number>();
    for (const m of j.meshes) for (const p of m.primitives) {
      for (const a of Object.values(p.attributes || {})) accUsed.add(a as number);
      if (p.indices !== undefined) accUsed.add(p.indices);
      for (const t of p.targets || []) for (const a of Object.values(t)) accUsed.add(a as number);
    }
    for (const s of j.skins) if (s.inverseBindMatrices !== undefined) accUsed.add(s.inverseBindMatrices);
    for (const an of j.animations || []) for (const s of an.samplers) { accUsed.add(s.input); accUsed.add(s.output); }
    const accMap = compact(j.accessors.length, accUsed);
    j.accessors = j.accessors.filter((_: any, i: number) => accUsed.has(i));
    const ra = (i: number) => accMap.get(i)!;
    for (const m of j.meshes) for (const p of m.primitives) {
      for (const k of Object.keys(p.attributes || {})) p.attributes[k] = ra(p.attributes[k]);
      if (p.indices !== undefined) p.indices = ra(p.indices);
      for (const t of p.targets || []) for (const k of Object.keys(t)) t[k] = ra(t[k]);
    }
    for (const s of j.skins) if (s.inverseBindMatrices !== undefined) s.inverseBindMatrices = ra(s.inverseBindMatrices);
    for (const an of j.animations || []) for (const s of an.samplers) { s.input = ra(s.input); s.output = ra(s.output); }

    // --- textures in use (materials + VRM0 material properties + VRM meta thumbnails) ---
    const texUsed = new Set<number>();
    const walkTex = (o: any, visit: (holder: any) => void) => {
      if (!o || typeof o !== 'object') return;
      for (const [k, v] of Object.entries(o)) {
        if (v && typeof v === 'object') {
          if (/Texture$/.test(k) && typeof (v as any).index === 'number') visit(v);
          else walkTex(v, visit);
        }
      }
    };
    for (const m of j.materials) walkTex(m, (h) => texUsed.add(h.index));
    const vrm0 = j.extensions?.VRM;
    for (const mp of vrm0?.materialProperties || []) for (const v of Object.values(mp.textureProperties || {})) texUsed.add(v as number);
    if (typeof vrm0?.meta?.texture === 'number' && vrm0.meta.texture >= 0) texUsed.add(vrm0.meta.texture);
    const vrm1 = j.extensions?.VRMC_vrm;
    const thumbImage1 = typeof vrm1?.meta?.thumbnailImage === 'number' ? vrm1.meta.thumbnailImage : -1;
    const texMap = compact(j.textures.length, texUsed);
    j.textures = j.textures.filter((_: any, i: number) => texUsed.has(i));
    for (const m of j.materials) walkTex(m, (h) => { h.index = texMap.get(h.index); });
    for (const mp of vrm0?.materialProperties || []) for (const k of Object.keys(mp.textureProperties || {})) mp.textureProperties[k] = texMap.get(mp.textureProperties[k]);
    if (typeof vrm0?.meta?.texture === 'number' && vrm0.meta.texture >= 0) vrm0.meta.texture = texMap.get(vrm0.meta.texture);

    // --- images in use ---
    const imgUsed = new Set<number>(j.textures.map((t: any) => t.source));
    if (thumbImage1 >= 0) imgUsed.add(thumbImage1);
    const imgMap = compact(j.images.length, imgUsed);
    j.images = j.images.filter((_: any, i: number) => imgUsed.has(i));
    for (const t of j.textures) t.source = imgMap.get(t.source);
    if (thumbImage1 >= 0) vrm1.meta.thumbnailImage = imgMap.get(thumbImage1);

    // --- buffer views in use → one new packed buffer ---
    const bvUsed = new Set<number>();
    for (const a of j.accessors) {
      if (a.bufferView !== undefined) bvUsed.add(a.bufferView);
      if (a.sparse) { bvUsed.add(a.sparse.indices.bufferView); bvUsed.add(a.sparse.values.bufferView); }
    }
    for (const im of j.images) if (im.bufferView !== undefined) bvUsed.add(im.bufferView);
    const order = [...bvUsed].sort((a, b) => a - b);
    const bvMap = new Map<number, number>();
    const parts: Uint8Array[] = [];
    let size = 0;
    const views: any[] = [];
    for (const old of order) {
      const bytes = this.bytesOfView(old);
      const pad = (4 - (size % 4)) % 4;
      if (pad) { parts.push(new Uint8Array(pad)); size += pad; }
      const v = { ...j.bufferViews[old], buffer: 0, byteOffset: size, byteLength: bytes.byteLength };
      bvMap.set(old, views.length);
      views.push(v);
      parts.push(bytes);
      size += bytes.byteLength;
    }
    const bin = new Uint8Array(size);
    let o = 0;
    for (const p of parts) { bin.set(p, o); o += p.byteLength; }
    j.bufferViews = views;
    for (const a of j.accessors) {
      if (a.bufferView !== undefined) a.bufferView = bvMap.get(a.bufferView);
      if (a.sparse) { a.sparse.indices.bufferView = bvMap.get(a.sparse.indices.bufferView); a.sparse.values.bufferView = bvMap.get(a.sparse.values.bufferView); }
    }
    for (const im of j.images) if (im.bufferView !== undefined) im.bufferView = bvMap.get(im.bufferView);
    j.buffers = [{ byteLength: size }];
    return { json: j, bin };
  }
}

function compact(n: number, used: Set<number>): Map<number, number> {
  const m = new Map<number, number>();
  let k = 0;
  for (let i = 0; i < n; i++) if (used.has(i)) m.set(i, k++);
  return m;
}

/** Index of the node named `name` (VRoid bone names are unique). */
export function nodeByName(json: any, name: string): number {
  return json.nodes.findIndex((n: any) => n.name === name);
}

/** World-space translation of a node (VRoid bones carry translation only). */
export function nodeWorldPosition(json: any, node: number): [number, number, number] {
  const parent = new Map<number, number>();
  json.nodes.forEach((n: any, i: number) => (n.children || []).forEach((c: number) => parent.set(c, i)));
  const p: [number, number, number] = [0, 0, 0];
  for (let i: number | undefined = node; i !== undefined; i = parent.get(i)) {
    const t = json.nodes[i].translation || [0, 0, 0];
    p[0] += t[0]; p[1] += t[1]; p[2] += t[2];
  }
  return p;
}
